import { lineColors } from "../constants";
import { IOrbitItem, IOrbitDataResponse, IChartData, IChartDataOrbit } from "../interfaces";

export const transformAmplitudePhaseToOrbit = (charts: IChartData[]): IChartDataOrbit[] => {
  if (!charts.length) return [];

  const result: IChartDataOrbit[] = [];

  for (let i = 0; i < charts.length; i += 2) {
    const amplitude = charts[i];
    const phase = charts[i + 1];

    if (!amplitude || !phase) continue;

    const byDate = new Map<string, { r?: number; theta?: number; rotationFrequencyRpm?: number | null }>();

    amplitude.items.forEach(({ date, value, rotationFrequencyRpm }) => {
      byDate.set(date, { r: value, rotationFrequencyRpm: rotationFrequencyRpm ?? null });
    });

    phase.items.forEach(({ date, value, rotationFrequencyRpm }) => {
      const entry = byDate.get(date) ?? {};
      entry.theta = value;
      if (entry.rotationFrequencyRpm == null && rotationFrequencyRpm != null) {
        entry.rotationFrequencyRpm = rotationFrequencyRpm;
      }
      byDate.set(date, entry);
    });

    const orbitItems: IOrbitItem[] = [];

    byDate.forEach((v, date) => {
      if (v.r == null || v.theta == null) return;

      const thetaRadians = (v.theta * Math.PI) / 180;

      orbitItems.push({
        index: orbitItems.length,
        x: Number((v.r * Math.cos(thetaRadians)).toFixed(4)),
        y: Number((v.r * Math.sin(thetaRadians)).toFixed(4)),
        r: v.r,
        theta: v.theta,
        date: new Date(date),
        rotationFrequencyRpm: v.rotationFrequencyRpm ?? null,
      });
    });

    if (!orbitItems.length) continue;

    result.push({
      name: amplitude.name,
      color: amplitude.color,
      units: amplitude.units,
      unitCode: amplitude.unitCode ?? null,
      parameterName: amplitude.parameterName,
      parameterLabel: amplitude.parameterLabel,
      idPoint: amplitude.idPoint,
      items: orbitItems,
    });
  }

  return result;
};

type OrbitItem = {
  index: number;
  x: number;
  y: number;
  theta: number;
  r: number;
  isZero: boolean;
};

export const cartesianToPolar = (xData: number[], yData: number[], zeroIndexes: number[]): OrbitItem[] => {
  const zeroIndexSet = new Set(zeroIndexes);

  return xData.map((x, i) => {
    const y = yData[i];

    const r = Math.sqrt(x * x + y * y);

    let theta = (Math.atan2(y, x) * 180) / Math.PI;
    if (theta < 0) theta += 360;

    return {
      index: i,
      x: Number(x.toFixed(4)),
      y: Number(y.toFixed(4)),
      theta: Math.round(theta),
      r: Number(r.toFixed(1)),
      isZero: zeroIndexSet.has(i),
    };
  });
};

export const transformOrbitDataToChart = (measure: IOrbitDataResponse, colorIndex: number): IChartDataOrbit => {
  const {
    xData,
    yData,
    name,
    units,
    Xmax,
    Ymax,
    Smax,
    zeroIndexes,
    resolution,
    param3,
    param3DataMs,
    rangeType,
    rotationFrequencyHz,
    xPickupAngle,
    yPickupAngle,
  } = measure;
  const items = cartesianToPolar(xData, yData, zeroIndexes);

  return {
    color: lineColors[colorIndex % lineColors.length],
    name,
    items,
    units: String(units),
    unitCode: units,
    Xmax,
    Ymax,
    Smax,
    xData,
    yData,
    zeroIndexes,
    resolution,
    param3,
    param3DataMs: param3DataMs ?? null,
    rangeType,
    rotationFrequencyHz,
    xPickupAngle,
    yPickupAngle,
  };
};
